'use client'

import React, { useCallback, useEffect, useState } from 'react'
import Image from 'next/image'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { useThrottledCallback } from 'use-debounce'
import AnimatedSection from '@/components/AnimatedSection'
import styles from './styles.module.css'

interface Stage {
	title: string
	description: string
}

const Lending: React.FC = () => {
	const stages: Stage[] = [
		{
			title: 'до 5 млн рублей',
			description: 'Возобновляемый кредитный лимит рассчитывается и доступен для всех ваших покупателей автоматически прямо в платёжном окне по ссылке или QR-коду.'
		},
		{
			title: 'Одобрение за 3 минуты',
			description: 'Банк принимает решение о финансировании без залогов, поручителей и сбора документов. Скоринг проходит в фоновом режиме в момент открытия ссылки.'
		},
		{
			title: 'Деньги сразу у вас',
			description: 'Вы больше не кредитуете клиентов за свой счёт и не тратите ресурсы на возврат долгов. Все риски берёт на себя банк, а для вас это обычный платёж.'
		}
	]

	const [emblaRef, emblaApi] = useEmblaCarousel({
		dragFree: true,
		align: 'start',
		breakpoints: { '(min-width: 1440px)': { active: false } }
	})

	const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
	const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

	const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
		setPrevBtnDisabled(!emblaApi.canScrollPrev())
		setNextBtnDisabled(!emblaApi.canScrollNext())
	}, [])

	const onSelectThrottled = useThrottledCallback(onSelect, 100)

	useEffect(() => {
		if (!emblaApi) return

		onSelect(emblaApi)
		emblaApi.on('reInit', onSelect).on('select', onSelect)
	}, [emblaApi, onSelect, onSelectThrottled])

	const onPrevButtonClick = useCallback(() => {
		if (!emblaApi) return
		emblaApi.scrollPrev()
	}, [emblaApi])

	const onNextButtonClick = useCallback(() => {
		if (!emblaApi) return
		emblaApi.scrollNext()
	}, [emblaApi])

	return (
		<section className={styles.lending}>
			<AnimatedSection animation="slideUp">
				<div className={styles.subtitle}>Кредитование ваших клиентов</div>
				<h2
					className={styles.title}
					dangerouslySetInnerHTML={{ __html: '<span>Выделенная кредитная линия</span> для ваших клиентов: <span>гарантия оплаты</span> без задержек и задолженности' }}
				/>
				<p
					className={styles.description}
					dangerouslySetInnerHTML={{ __html: 'Покупатели часто замораживают сделки из-за временного отсутствия свободных денег или кассовых разрывов. <span>Встроенный модуль онлайн-кредитования</span> позволяет вашим клиентам оплачивать счета в рассрочку, <span>пока вы получаете 100% суммы на счёт сразу</span> после одобрения.' }}
				/>
			</AnimatedSection>
			<AnimatedSection animation="slideUp">
				<div className={styles.stagesSlider}>
					<div className={styles.stagesSliderContainer}>
						<div className={styles.embla}>
							<div className={styles.embla__viewport} ref={emblaRef}>
								<div className={styles.embla__container}>
									{stages.map((stage, index) => {
										const imagePaths = [
											'/images/b2b/lending_1.png',
											'/images/b2b/lending_2.png',
											'/images/b2b/lending_3.png'
										]

										return (
											<div key={index} className={styles.embla__slide}>
												<div className={styles.stageCard}>
													<div className={styles.stageImage}>
														<Image
															src={imagePaths[index]}
															alt={stage.title}
															width={310}
															height={200}
															className={styles.image}
														/>
													</div>
													<h3 className={styles.stageTitle} dangerouslySetInnerHTML={{ __html: stage.title }}></h3>
													<p className={styles.stageDescription}>{stage.description}</p>
												</div>
											</div>
										)
									})}
								</div>
							</div>

							{!prevBtnDisabled && (
								<button
									className={styles.embla__prev}
									onClick={onPrevButtonClick}
								>
									<Image
										src="/images/portfolio/lk-agro-b2b/slider-button-left.svg"
										alt="Previous"
										width={34}
										height={34}
									/>
								</button>
							)}

							{!nextBtnDisabled && (
								<button
									className={styles.embla__next}
									onClick={onNextButtonClick}
								>
									<Image
										src="/images/portfolio/lk-agro-b2b/slider-button-right.svg"
										alt="Next"
										width={34}
										height={34}
									/>
								</button>
							)}
						</div>
					</div>
				</div>
			</AnimatedSection>
		</section>
	)
}

Lending.displayName = 'Lending'

export default Lending
